// AppRoutes.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import { isAdmin, isCreativeUser } from "../utils/buttonVisibility";

import Dashboard from "../pages/Dashboard";
import QuotationManagement from "../pages/QuotationManagement";
import CreateQuotation from "../pages/CreateQuotation";
import EditQuotation from "../pages/EditQuotation";
import QuotationDetails from "../pages/QuotationDetails";
import ApprovalManagement from "../pages/ApprovalManagement";
import UserManagement from "../pages/UserManagement";
import Logs from "../pages/Logs";
import Login from "../pages/Login";
import Profile from "../pages/Profile";
import ManageFlows from "../pages/ManageFlows";
import ApproveFlow from "../pages/ApproveFlow";
import EditFlow from "../pages/EditFlow";
import RequestApproveFlow from "../pages/RequestApproveFlow";
import RepairApprovalFlow from "../pages/RepairApprovalFlow";
import ResetPassword from "../pages/ResetPassword";
import RequestPasswordReset from "../pages/RequestPasswordReset";
import EditUser from "../pages/EditUser";
import ClientManagement from "../pages/ClientManagement";
import EditClient from "../pages/EditClient";
import CreateClient from "../pages/CreateClient";
import CreateUser from "../pages/CreateUser";
import CreateTeam from "../pages/CreateTeam";
import MeetingRooms from "../pages/MeetingRooms";
import TravelExpense from "../pages/TravelExpense";
import TravelExpenseRequest from "../pages/TravelExpenseRequest";
import TravelExpenseApprovalPage from "../pages/TravelExpenseApprovalPage";
import TravelExpenseLogs from "../pages/TravelExpenseLogs";
import TimesheetPage from "../pages/timesheet/TimesheetPage";
import CustomerSign from "../pages/CustomerSign";
import AnnouncementManagement from "../pages/AnnouncementManagement";
import AnnouncementFormPage from "../pages/AnnouncementFormPage";
import AnnouncementLogs from "../pages/AnnouncementLogs";
import TimesheetDashboardPage from "../pages/timesheet/TimesheetDashboardPage";
import TimesheetApprovalsPage from "../pages/timesheet/TimesheetApprovalsPage";
import TimesheetApprovalDetailPage from "../pages/timesheet/TimesheetApprovalDetailPage";
import TimesheetReopenPage from "../pages/timesheet/TimesheetReopenPage";

import Layout from "../components/Layout";

const CreativeRouteGuard = ({ children, blockCreative = false }) => {
  const location = useLocation();

  if (
    blockCreative &&
    isCreativeUser() &&
    location.pathname !== "/profile"
  ) {
    return <Navigate to="/profile" replace />;
  }

  return children;
};

const AdminRouteGuard = ({ children }) => {
  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const withLayout = (Component) => (
  <ProtectedRoute>
    <Layout>
      <Component />
    </Layout>
  </ProtectedRoute>
);

const withLayoutGuard = (Component, { blockCreative = false } = {}) => (
  <ProtectedRoute>
    <CreativeRouteGuard blockCreative={blockCreative}>
      <Layout>
        <Component />
      </Layout>
    </CreativeRouteGuard>
  </ProtectedRoute>
);

const withAdminLayoutGuard = (Component, { blockCreative = false } = {}) => (
  <ProtectedRoute>
    <CreativeRouteGuard blockCreative={blockCreative}>
      <AdminRouteGuard>
        <Layout>
          <Component />
        </Layout>
      </AdminRouteGuard>
    </CreativeRouteGuard>
  </ProtectedRoute>
);

const AppRoutes = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/request-password-reset"
          element={<RequestPasswordReset />}
        />
        <Route path="/customer-sign/:token" element={<CustomerSign />} />

        <Route
          path="/"
          element={withLayoutGuard(Dashboard, { blockCreative: true })}
        />
        <Route path="/meeting-rooms" element={withLayout(MeetingRooms)} />

        <Route
          path="/quotations"
          element={withLayoutGuard(QuotationManagement, {
            blockCreative: true,
          })}
        />
        <Route
          path="/quotations/create"
          element={withLayoutGuard(CreateQuotation, { blockCreative: true })}
        />
        <Route
          path="/request-approve-flow/:quotationId"
          element={withLayoutGuard(RequestApproveFlow, {
            blockCreative: true,
          })}
        />
        <Route
          path="/quotations/edit/:id"
          element={withLayoutGuard(EditQuotation, { blockCreative: true })}
        />
        <Route
          path="/quotations/:id"
          element={withLayoutGuard(QuotationDetails, { blockCreative: true })}
        />

        <Route
          path="/approvals"
          element={withLayoutGuard(ApprovalManagement, {
            blockCreative: true,
          })}
        />
        <Route
          path="/approve-flow"
          element={withLayoutGuard(ApproveFlow, { blockCreative: true })}
        />
        <Route
          path="/manage-flows"
          element={withLayoutGuard(ManageFlows, { blockCreative: true })}
        />
        <Route
          path="/repair-approval-flow"
          element={withAdminLayoutGuard(RepairApprovalFlow, {
            blockCreative: true,
          })}
        />
        <Route
          path="/edit-flow/:id"
          element={withLayoutGuard(EditFlow, { blockCreative: true })}
        />

        <Route
          path="/users"
          element={withLayoutGuard(UserManagement, { blockCreative: true })}
        />
        <Route
          path="/logs"
          element={withLayoutGuard(Logs, { blockCreative: true })}
        />
        <Route path="/profile" element={withLayout(Profile)} />
        <Route path="/timesheets" element={withLayout(TimesheetPage)} />
        <Route
          path="/timesheets/dashboard"
          element={withLayout(TimesheetDashboardPage)}
        />
        <Route
          path="/timesheets/dashboard/:userId"
          element={withLayout(TimesheetDashboardPage)}
        />
        <Route
          path="/timesheets/approvals"
          element={withLayout(TimesheetApprovalsPage)}
        />
        <Route
          path="/timesheets/approvals/:submissionId"
          element={withLayout(TimesheetApprovalDetailPage)}
        />
        <Route
          path="/timesheets/reopen"
          element={withAdminLayoutGuard(TimesheetReopenPage)}
        />
        <Route
          path="/quotation-details/:id"
          element={withLayoutGuard(QuotationDetails, { blockCreative: true })}
        />
        <Route
          path="/edit-user/:id"
          element={withLayoutGuard(EditUser, { blockCreative: true })}
        />
        <Route
          path="/create-user"
          element={withLayoutGuard(CreateUser, { blockCreative: true })}
        />

        <Route
          path="/clients"
          element={withLayoutGuard(ClientManagement, { blockCreative: true })}
        />
        <Route
          path="/edit-client/:id"
          element={withLayoutGuard(EditClient, { blockCreative: true })}
        />
        <Route
          path="/create-client"
          element={withLayoutGuard(CreateClient, { blockCreative: true })}
        />

        <Route
          path="/create-team"
          element={withLayoutGuard(CreateTeam, { blockCreative: true })}
        />
        <Route
          path="/announcement-management"
          element={withAdminLayoutGuard(AnnouncementManagement, {
            blockCreative: true,
          })}
        />
        <Route
          path="/announcement-management/create"
          element={withAdminLayoutGuard(AnnouncementFormPage, {
            blockCreative: true,
          })}
        />
        <Route
          path="/announcement-management/:id"
          element={withAdminLayoutGuard(AnnouncementFormPage, {
            blockCreative: true,
          })}
        />
        <Route
          path="/announcement-management/:id/edit"
          element={withAdminLayoutGuard(AnnouncementFormPage, {
            blockCreative: true,
          })}
        />
        <Route
          path="/announcement-management/:id/logs"
          element={withAdminLayoutGuard(AnnouncementLogs, {
            blockCreative: true,
          })}
        />

        <Route path="/travel-expense" element={withLayout(TravelExpense)} />
        <Route
          path="/travel-expense/request"
          element={withLayout(TravelExpenseRequest)}
        />
        <Route
          path="/travel-expense/request/:id"
          element={withLayout(TravelExpenseRequest)}
        />
        <Route
          path="/travel-expense/approve"
          element={withLayout(TravelExpenseApprovalPage)}
        />
        <Route
          path="/travel-expense/logs"
          element={withLayout(TravelExpenseLogs)}
        />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
